import { useState, useMemo } from "react";
import { useLoanRepayments, useApproveRepayment, useRejectRepayment, useRecordLoanRepayment, useSendLoanNotification } from "@/hooks/useLoans";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader, Calendar, DollarSign, AlertTriangle, CheckCircle2, Clock,
  Phone, Send, Search, Download, Printer, ChevronLeft, ChevronRight,
  MessageSquare, AlertCircle, XCircle, HandCoins, Shield, Check,
} from "lucide-react";
import { format, addMonths, subMonths } from "date-fns";
import { formatCurrency, cn } from "@/lib/utils";
import { toast } from "sonner";

export default function MonthlyRepayments() {
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState<any>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentInstallment, setPaymentInstallment] = useState(1);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyType, setNotifyType] = useState("repayment_reminder");
  const [notifyMessage, setNotifyMessage] = useState("");

  const { data, isLoading } = useLoanRepayments(currentMonth, currentYear, activeTab);
  const approveRepayment = useApproveRepayment();
  const rejectRepayment = useRejectRepayment();
  const recordRepayment = useRecordLoanRepayment();
  const sendNotification = useSendLoanNotification();

  const repayments = data?.repayments || [];
  const summary = data?.summary || {};

  const filteredRepayments = useMemo(() => {
    if (!search) return repayments;
    const q = search.toLowerCase();
    return repayments.filter((r: any) =>
      r.member?.name?.toLowerCase().includes(q) ||
      r.member?.memberId?.toLowerCase().includes(q) ||
      r.loan?.loanCode?.toLowerCase().includes(q) ||
      r.repaymentCode?.toLowerCase().includes(q)
    );
  }, [repayments, search]);

  const statusColors: Record<string, string> = {
    pending_verification: "bg-amber-100 text-amber-700 border-amber-200",
    approved: "bg-green-100 text-green-700 border-green-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
    sent_to_payroll: "bg-blue-100 text-blue-700 border-blue-200",
    deducted: "bg-purple-100 text-purple-700 border-purple-200",
    applied_to_loan: "bg-green-100 text-green-700 border-green-200",
  };

  const handleRecordPayment = async () => {
    if (!selectedRepayment || !paymentAmount || Number(paymentAmount) <= 0) return;
    try {
      await recordRepayment.mutateAsync({
        loanId: selectedRepayment.loan?.id,
        payload: {
          amount: Number(paymentAmount),
          installmentNumber: paymentInstallment,
          paymentMethod,
          paymentDate: new Date().toISOString().split("T")[0],
          notes: paymentNotes,
          verifyImmediately: true,
        },
      });
      toast.success("Payment recorded and verified");
      setPaymentModalOpen(false);
      setSelectedRepayment(null);
      setPaymentAmount("");
      setPaymentNotes("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to record payment");
    }
  };

  const handleApprove = async (repayment: any) => {
    try {
      await approveRepayment.mutateAsync(repayment.id);
      toast.success("Repayment approved" + (repayment.paymentMethod === "Salary Deduction" ? ". Sent to HR." : " and applied to loan."));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to approve");
    }
  };

  const handleReject = async () => {
    if (!selectedRepayment) return;
    try {
      await rejectRepayment.mutateAsync({ id: selectedRepayment.id, reason: rejectReason });
      toast.success("Repayment rejected");
      setRejectModalOpen(false);
      setSelectedRepayment(null);
      setRejectReason("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to reject");
    }
  };

  const handleSendNotification = async () => {
    if (!selectedRepayment) return;
    try {
      await sendNotification.mutateAsync({
        loanId: selectedRepayment.loan?.id,
        payload: { type: notifyType, message: notifyMessage, channel: "in-app" },
      });
      toast.success("Notification sent");
      setNotifyModalOpen(false);
      setNotifyMessage("");
    } catch {
      toast.error("Failed to send notification");
    }
  };

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  return (
    <div className="sm:px-4 px-3 pt-3 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Monthly Repayments</h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage loan repayments with verification workflow</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Print
          </Button>
        </div>
      </div>

      {/* Month Navigator */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" onClick={prevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <h2 className="text-lg font-semibold">{format(new Date(currentYear, currentMonth - 1), "MMMM yyyy")}</h2>
        </div>
        <Button variant="outline" size="sm" onClick={nextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Total</span>
              <DollarSign className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-lg font-bold text-blue-700">{summary.total || 0}</p>
            <p className="text-[10px] text-gray-400">{formatCurrency(summary.totalAmount || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Pending</span>
              <Clock className="h-4 w-4 text-amber-600" />
            </div>
            <p className="text-lg font-bold text-amber-700">{summary.pending || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Approved</span>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-lg font-bold text-green-700">{summary.approved || 0}</p>
            <p className="text-[10px] text-gray-400">{formatCurrency(summary.approvedAmount || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Rejected</span>
              <XCircle className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-lg font-bold text-red-700">{summary.rejected || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Collection</span>
              <HandCoins className="h-4 w-4 text-[#009A44]" />
            </div>
            <p className="text-lg font-bold text-[#009A44]">
              {summary.totalAmount > 0 ? Math.round((summary.approvedAmount / summary.totalAmount) * 100) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs + Search + Record Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({summary.total || 0})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({summary.pending || 0})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({summary.approved || 0})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({summary.rejected || 0})</TabsTrigger>
            <TabsTrigger value="salary_deduction">Salary Deduction</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search member, loan code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-56"
            />
          </div>
          <Button
            size="sm"
            className="bg-[#009A44] hover:bg-[#008238] text-white"
            onClick={() => {
              setPaymentAmount("");
              setPaymentInstallment(1);
              setPaymentMethod("Cash");
              setPaymentNotes("");
              setPaymentModalOpen(true);
            }}
          >
            <DollarSign className="h-3.5 w-3.5 mr-1" />
            Record Payment
          </Button>
        </div>
      </div>

      {/* Repayments Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-green-600" />
        </div>
      ) : filteredRepayments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No repayments found for this period</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Code</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Member</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Loan</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Installment</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Amount</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Method</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Status</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Payroll</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRepayments.map((r: any) => (
                  <tr key={r.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-3 font-mono text-xs">{r.repaymentCode}</td>
                    <td className="py-3 px-3">
                      <p className="font-medium">{r.member?.name || "Unknown Member"}</p>
                      <p className="text-[10px] text-gray-400">{r.member?.memberId}</p>
                    </td>
                    <td className="py-3 px-3 font-mono text-xs">{r.loan?.loanCode || <StatusBadge status="pending" size="sm" />}</td>
                    <td className="py-3 px-3 text-center">#{r.installmentNumber}</td>
                    <td className="py-3 px-3 text-right font-bold text-[#009A44]">{formatCurrency(r.amount)}</td>
                    <td className="py-3 px-3 text-xs">
                      <Badge variant="outline" className="text-[9px]">{r.paymentMethod}</Badge>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Badge className={cn("text-[9px] border", statusColors[r.status])}>
                        {r.status === "pending_verification" && <Clock className="h-3 w-3 mr-1" />}
                        {r.status === "approved" && <Check className="h-3 w-3 mr-1" />}
                        {r.status === "rejected" && <XCircle className="h-3 w-3 mr-1" />}
                        {r.status.replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {r.payrollStatus && (
                        <Badge variant="outline" className="text-[9px]">{r.payrollStatus.replace(/_/g, " ")}</Badge>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        {r.status === "pending_verification" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-green-700 hover:bg-green-50"
                              onClick={() => handleApprove(r)}
                              disabled={approveRepayment.isLoading}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setSelectedRepayment(r);
                                setRejectModalOpen(true);
                              }}
                            >
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            setSelectedRepayment(r);
                            setNotifyModalOpen(true);
                          }}
                        >
                          <Send className="h-3 w-3" />
                        </Button>
                        {r.member?.phone && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => window.open(`tel:${r.member.phone}`)}
                          >
                            <Phone className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Record Payment Modal (Admin) */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Loan Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Loan ID / Code</Label>
              <Input
                value={selectedRepayment?.loan?.loanCode || ""}
                disabled
                className="bg-gray-50"
              />
            </div>
            <div>
              <Label>Installment Number *</Label>
              <Input
                type="number"
                value={paymentInstallment}
                onChange={(e) => setPaymentInstallment(parseInt(e.target.value) || 1)}
                min={1}
              />
            </div>
            <div>
              <Label>Amount (FRW) *</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                min={1}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Salary Deduction">Salary Deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Payment reference, receipt number..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleRecordPayment}
              disabled={!paymentAmount || Number(paymentAmount) <= 0 || recordRepayment.isLoading}
            >
              {recordRepayment.isLoading ? <Loader className="h-4 w-4 animate-spin mr-2" /> : null}
              Record & Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Repayment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reject repayment <strong>{selectedRepayment?.repaymentCode}</strong> of <strong>{formatCurrency(selectedRepayment?.amount || 0)}</strong>?
            </p>
            <div>
              <Label>Reason</Label>
              <Textarea
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleReject}
              disabled={rejectRepayment.isLoading}
            >
              {rejectRepayment.isLoading ? <Loader className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Modal */}
      <Dialog open={notifyModalOpen} onOpenChange={setNotifyModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              To: <strong>{selectedRepayment?.member?.name}</strong> | Loan: <strong>{selectedRepayment?.loan?.loanCode}</strong>
            </p>
            <div>
              <Label>Notification Type</Label>
              <Select value={notifyType} onValueChange={setNotifyType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="repayment_reminder">Repayment Reminder</SelectItem>
                  <SelectItem value="overdue_warning">Overdue Warning</SelectItem>
                  <SelectItem value="approval_notice">Approval Notice</SelectItem>
                  <SelectItem value="rejection_notice">Rejection Notice</SelectItem>
                  <SelectItem value="disbursement_notice">Disbursement Notice</SelectItem>
                  <SelectItem value="penalty_notice">Penalty Notice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Custom Message (optional)</Label>
              <Input
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Leave empty for default"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyModalOpen(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSendNotification}
              disabled={sendNotification.isLoading}
            >
              {sendNotification.isLoading ? <Loader className="h-4 w-4 animate-spin mr-2" /> : null}
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
